import * as _ from 'lodash';
import {EnumScheduleRepeatType, IChannel, IChannelItem, ICRUDCommand, ICRUDRecord, IScheduleEvent, ITimeline} from "../interfaces";
import {MAX_PLAYLIST_TIME, SCHEDULE_COLORS, TIMELINE_GRID_H, TIMELINE_SCALE} from "./global-constants";
import {dateAfter, dateToString} from "./date";
import {isScheduleEventsNull} from "./functions";
import {Lib} from "../../libs/Lib";

/**
 * Schedule -> timeline helpers. The full scheduler editor (scheduler-editor) renders app-scheduler-timeline from
 * them, and the scheduler insights band under the scene canvas (scheduler-insights, via schedule-insights.ts) derives
 * its "plays today" numbers from the same day expansion, so the mapping from schedule events to rows/blocks, and
 * from a dragged/resized block back to event fields, lives here once.
 */

// icon shown on an event block, keyed by the event_value type
export const SCHEDULE_EVENT_ICONS = {
    scene: 'control_camera',
    resource: 'photo_library',
    event: 'alarm_add'
};

// block height: the rarer the repeat type the taller the block, so stacked overlapping blocks stay visible
export const SCHEDULE_ITEM_UNIT_H = 6;
export const SCHEDULE_ITEM_MIN_H = 18;

// weekly_days bitmask, Sunday first (1 = Sun ... 64 = Sat)
export const SCHEDULE_WEEK_BITS = [1, 2, 4, 8, 16, 32, 64];

export interface IBuildScheduleTimelineOpts {
    events: IScheduleEvent[];
    dateArray: string[];           // one channel row per date (dateToString() values)
    zoom: number;
    specificDays?: string[];       // schedule.specific_days -> highlighted channel titles
    specificColor?: string;
    selectedEventId?: string;      // keeps the selected block highlighted across rebuilds
    activeChannel?: IChannel;      // keeps the selected channel row highlighted across rebuilds
}

export interface IScheduleEventUpdate {
    updatedFields: Array<{key: string, value: any}>;   // actionUpdateScheduleEvent payload (toScheduleEventBody() -> CRUD body)
    changed: boolean;                                  // false when the block landed where it already was
}

// event_value is a JSON string ({type, id, name}); a legacy row may hold a plain string or truncated JSON, and one
// bad row must not take the whole timeline down - it falls back to the "new event" defaults instead
export function parseScheduleEventValue(raw: any): any {
    if (!raw) return null;
    if (typeof raw !== 'string') return raw;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/**
 * Expand a schedule's events into timeline channels (one per date) and blocks (one per occurrence on
 * that date: an hourly event becomes 23 blocks, a weekly event only lands on its weekdays, etc).
 */
export function buildScheduleTimelineState(opts: IBuildScheduleTimelineOpts): ITimeline {
    const {dateArray, zoom, selectedEventId, activeChannel} = opts;
    const items: Array<IChannelItem> = [];
    const channels: Array<IChannel> = [];
    const specificDays = opts.specificDays || [];
    const specificColor = opts.specificColor || '';
    const colorDays = specificDays.map((v) => dateToString(new Date(v), true));

    // create channels
    dateArray.forEach((d, channelIndex) => {
        const name = d.slice(0, 10);
        channels.push({
            id: channelIndex,
            name,
            selected: activeChannel?.name == name ? activeChannel.selected : false,
            top: TIMELINE_GRID_H * channelIndex,
            color: colorDays.indexOf(name) > -1 ? specificColor : '',
            date: d
        });
    });
    let itemCount = 0;

    // sort a copy: the caller's array (often a store slice) stays untouched
    const events = [...(opts.events || [])].sort((a, b) => {
        if (a.repeat_type === b.repeat_type && a.repeat_type === EnumScheduleRepeatType.HOURLY) {
            return a.hourly_offset - b.hourly_offset;
        } else {
            return a.time - b.time;
        }
    });

    // create channel items
    events.forEach((i_event, eventIndex) => {
        if (!i_event.start_date) return;

        const startDate = dateToString(i_event.start_date);
        const repeatType = Math.floor(i_event.repeat_type);
        const forever = i_event.repeat_forever;
        const color = i_event.event_color ? Lib.DecimalToHex(i_event.event_color) : SCHEDULE_COLORS[repeatType];
        const selected = selectedEventId != null ? selectedEventId == i_event.schedule_event_id : false;
        const zIndex = 1000 * (6 - repeatType) + eventIndex;

        const eventValue = parseScheduleEventValue(i_event.event_value);
        const label = eventValue ? eventValue.name : "new event";
        const icon = SCHEDULE_EVENT_ICONS[eventValue ? eventValue.type : "scene"];

        let endDate;
        if (repeatType === EnumScheduleRepeatType.ONCE) {
            endDate = startDate;
        } else if (forever) {
            endDate = dateToString(dateAfter(i_event.start_date, {'years': 10}));
        } else if (i_event.end_date) {
            endDate = dateToString(i_event.end_date);
        } else {
            endDate = startDate;
        }

        dateArray.forEach((i_dt, channelIndex) => {
            if (i_dt < startDate || i_dt > endDate) {
                return;
            }

            const tempItem: IChannelItem = {
                start: i_event.time,
                duration: i_event.duration,
                height: SCHEDULE_ITEM_MIN_H + SCHEDULE_ITEM_UNIT_H * repeatType,
                top: TIMELINE_GRID_H * channelIndex,
                channel: channelIndex,
                type: repeatType,
                eventId: i_event.schedule_event_id,
                color,
                label,
                selected,
                zIndex,
                icon
            };

            switch (repeatType) {
                case EnumScheduleRepeatType.ONCE:
                case EnumScheduleRepeatType.DAILY: { // once or daily
                    itemCount += 1;
                    tempItem.itemId = itemCount;
                    items.push(tempItem);
                    break;
                }
                case EnumScheduleRepeatType.HOURLY: { // hourly
                    for (let t = 0; t < 23; t++) {
                        itemCount += 1;
                        const hourlyItem = _.clone(tempItem);
                        hourlyItem.start = t * 3600 + i_event.hourly_offset;
                        hourlyItem.itemId = itemCount;
                        items.push(hourlyItem);
                    }
                    break;
                }
                case EnumScheduleRepeatType.WEEKLY: { // weekly
                    let eventWeekDays = i_event.weekly_days;
                    if (!eventWeekDays) {
                        eventWeekDays = 0;
                    }

                    SCHEDULE_WEEK_BITS.forEach((w, dayIndex) => {
                        // tslint:disable-next-line
                        if (w === (w & eventWeekDays)) {
                            if (new Date(i_dt).getDay() == dayIndex) {
                                itemCount += 1;
                                tempItem.itemId = itemCount;
                                items.push(tempItem);
                            }
                        }
                    });
                    break;
                }
                case EnumScheduleRepeatType.MONTHLY: { // monthly
                    if (new Date(i_dt).getDate() == i_event.monthly_offset) {
                        itemCount += 1;
                        tempItem.itemId = itemCount;
                        items.push(tempItem);
                    }
                    break;
                }
                case EnumScheduleRepeatType.YEARLY: { // yearly
                    if (new Date(i_dt).getMonth() == i_event.yearly_offset && new Date(i_dt).getDate() == i_event.monthly_offset) {
                        itemCount += 1;
                        tempItem.itemId = itemCount;
                        items.push(tempItem);
                    }
                    break;
                }
                default:
                    break;
            }
        });
    });

    return {
        items,
        channels,
        gridWidth: MAX_PLAYLIST_TIME * TIMELINE_SCALE,
        zoom
    };
}

/**
 * After a drag/resize on one channel row: clamp its blocks into the day, order them by start and keep ONE
 * block per event (an hourly event is 23 blocks on the row - a single field update covers them all).
 */
export function clampScheduleChannelItems(items: IChannelItem[], channelId: number): IChannelItem[] {
    return (items || [])
        .filter((item) => item.channel == channelId)
        .map((item) => {
            if (item.start < 0) item.start = 0;
            if (item.start + item.duration > MAX_PLAYLIST_TIME) item.duration = MAX_PLAYLIST_TIME - item.start;
            return item;
        })
        .sort((a, b) => a.start - b.start)
        .filter((item, index, arr) => index === arr.findIndex((_item) => (item.eventId === _item.eventId)));
}

/**
 * Map a moved/resized timeline block back onto its schedule event: time/duration (or hourly offset), and
 * when the block was dragged onto another date row, the date-bound fields of its repeat type.
 * fromChannel = the row the block was on before the drag (null when only resized in place).
 */
export function computeScheduleEventUpdate(item: IChannelItem, event: IScheduleEvent, dateArray: string[], fromChannel: number = null): IScheduleEventUpdate {
    const newChannel = Math.floor(item.top / TIMELINE_GRID_H);
    const updatedFields: Array<{key: string, value: any}> = [];

    if (item.type == EnumScheduleRepeatType.HOURLY) {
        const hourly_offset = Math.min(Math.floor(item.start), 3600 - 1);  // hourly offset should be less than 1h
        const duration = Math.min(Math.floor(item.duration), 3600 - 1);  // duration should be less than 1h

        updatedFields.push(
            {
                key: 'duration',
                value: duration
            },
            {
                key: 'hourly_offset',
                value: hourly_offset
            }
        );
    } else {
        updatedFields.push(
            {
                key: 'duration',
                value: Math.floor(item.duration)
            },
            {
                key: 'time',
                value: Math.floor(item.start)
            }
        );
    }

    // change schedule start date by dragging event
    if (fromChannel != null && newChannel != fromChannel) {
        const newDate = new Date(dateArray[newChannel]);
        const oldDate = new Date(dateArray[fromChannel]);
        const formattedNewDate = dateToString(newDate);

        if (item.type === EnumScheduleRepeatType.ONCE || event.start_date > formattedNewDate) {
            updatedFields.push({
                key: 'start_date',
                value: formattedNewDate
            });
        }
        if (item.type === EnumScheduleRepeatType.ONCE || event.end_date < formattedNewDate) {
            updatedFields.push({
                key: 'end_date',
                value: formattedNewDate
            });
        }

        switch (item.type) {
            case EnumScheduleRepeatType.MONTHLY: {
                // change only date
                updatedFields.push({
                    key: 'monthly_offset',
                    value: newDate.getDate()
                });
                break;
            }
            case EnumScheduleRepeatType.YEARLY: {
                // change only date and month
                updatedFields.push(
                    {
                        key: 'monthly_offset',
                        value: newDate.getDate()
                    },
                    {
                        key: 'yearly_offset',
                        value: newDate.getMonth()
                    }
                );
                break;
            }
            case EnumScheduleRepeatType.WEEKLY: {
                const weekly_days = moveScheduleWeekDays(event.weekly_days, oldDate.getDay(), newDate.getDay());
                updatedFields.push({
                    key: 'weekly_days',
                    value: weekly_days
                });
                break;
            }
            case EnumScheduleRepeatType.ONCE:
            case EnumScheduleRepeatType.DAILY:
            case EnumScheduleRepeatType.HOURLY: {
                break;
            }
        }
    }

    const changed = updatedFields.some((e) => event[e.key] != e.value);
    return {updatedFields, changed};
}

// the CRUD body for an event update: {field: value} from the store payload
export function toScheduleEventBody(updatedFields: Array<{key: string, value: any}>): {[key: string]: any} {
    const body = {};
    (updatedFields || []).forEach((f) => body[f.key] = f.value);
    return body;
}

// schedule_events CRUD record (add / update / delete) - built in one place for the scheduler page and the docked band
export function buildScheduleEventCrud(studioId: string, command: ICRUDCommand, id: any, body: any): ICRUDRecord {
    return {
        studioId,
        command,
        primary: 'schedule_event_id',
        table: 'schedule_events',
        body: isScheduleEventsNull(body),
        id
    };
}

// re-save of the parent schedule record after its events changed: the empty-body update the scheduler page has
// always dispatched when leaving a schedule it edited (onSaveOpenEdit)
export function buildScheduleTouchCrud(studioId: string, scheduleId: any): ICRUDRecord {
    return {
        studioId,
        command: 'update',
        primary: 'schedule_id',
        table: 'schedules',
        id: scheduleId,
        body: {}
    };
}

// move one weekday bit of a weekly event's bitmask (1=Sun ... 64=Sat) from oldDay to newDay
export function moveScheduleWeekDays(accessMask: number, oldDay: number, newDay: number): number {
    if (oldDay == newDay) {
        return accessMask;
    }
    const bits = SCHEDULE_WEEK_BITS;
    const movedDay = bits[newDay];
    const checks: any[] = Lib.GetWeekDaysMask(accessMask, false); // false: return number array

    // only drop the old day when it is in the mask: an indexOf of -1 would splice the LAST day off instead
    const oldIndex = checks.indexOf(bits[oldDay]);
    if (oldIndex > -1) {
        checks.splice(oldIndex, 1);
    }
    if (checks.indexOf(movedDay) == -1) {
        checks.push(movedDay);
    }

    return checks.reduce((a, b) => a + b, 0);
}
